import { getTerraConfigValue } from './config-provider';

export const E2E_TEST_MODE_ENV_KEY = 'NEXT_PUBLIC_E2E_TEST_MODE' as const;
export const E2E_TEST_MODE_COOKIE_NAME = 'terra-e2e-test-mode' as const;
export const E2E_TEST_MODE_COOKIE_VALUE = '1' as const;

export type AutomationIdProps = {
  'data-testid'?: string;
};

const hasE2eTestModeCookie = (): boolean => {
  if (typeof document === 'undefined') {
    return false;
  }

  return document.cookie
    .split(';')
    .some(
      (entry) =>
        entry.trim() ===
        `${E2E_TEST_MODE_COOKIE_NAME}=${E2E_TEST_MODE_COOKIE_VALUE}`,
    );
};

const isEnvE2eTestMode = (): boolean =>
  getTerraConfigValue(E2E_TEST_MODE_ENV_KEY) === 'true';

let cookieAutomationIdsEnabled = false;
const automationIdEnabledListeners = new Set<() => void>();

const notifyAutomationIdEnabledListeners = (): void => {
  for (const listener of automationIdEnabledListeners) {
    listener();
  }
};

export const subscribeAutomationIdEnabled = (
  onStoreChange: () => void,
): (() => void) => {
  automationIdEnabledListeners.add(onStoreChange);

  return () => {
    automationIdEnabledListeners.delete(onStoreChange);
  };
};

export const enableCookieAutomationIdsAfterHydration = (): boolean => {
  if (isEnvE2eTestMode() || !hasE2eTestModeCookie()) {
    return false;
  }

  if (cookieAutomationIdsEnabled) {
    return true;
  }

  cookieAutomationIdsEnabled = true;
  notifyAutomationIdEnabledListeners();

  return true;
};

export const resetCookieAutomationIdsAfterHydration = (): void => {
  if (!cookieAutomationIdsEnabled) {
    return;
  }

  cookieAutomationIdsEnabled = false;
  notifyAutomationIdEnabledListeners();
};

export const isE2eTestMode = (): boolean => {
  if (isEnvE2eTestMode()) {
    return true;
  }

  return hasE2eTestModeCookie();
};

export const isAutomationIdEnabled = (): boolean => {
  if (isEnvE2eTestMode()) {
    return true;
  }

  return cookieAutomationIdsEnabled && hasE2eTestModeCookie();
};

export const getAutomationIdEnabledSnapshot = (): boolean =>
  isAutomationIdEnabled();

export const getAutomationIdEnabledServerSnapshot = (): boolean =>
  isEnvE2eTestMode();

export const composeAutomationId = (id: string, suffix?: string): string =>
  suffix ? `${id}-${suffix}` : id;

export const automationId = (
  id: string,
  suffix?: string,
): AutomationIdProps => {
  if (!id || id.length === 0 || !isAutomationIdEnabled()) {
    return {};
  }

  return { 'data-testid': composeAutomationId(id, suffix) };
};
